import { FC, useMemo, useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';

import {
  MDBox,
  MDTypography,
  DataTable,
  MDButton,
  FaIcon,
  CloseButton
} from '@components';

import { RootState } from '@store/store';
import { useLazyGetDataCleaningStatisticsQuery } from '@api/projects';
import { useLazyGetFeatureAccessQuery } from '@api/usePlans';

import { convertToExcelColumnNumber, parseJSON } from '@utils/common';
import {
  getCleanLabel,
  getFontBorderColor,
  getLabelColor
} from '@utils/cleaning';
import { sendAmplitudeData } from '@utils/amplitude';
import { CleanResponse, CleanedRow } from '@DStypes/project';
import { ColumnTypes, FeatureTypes } from '@constants/common';
import { CLICKED_DOWNLOAD_CLEANED_FILE } from '@constants/eventNames';

interface CleanActionsModalProps {
  open: boolean;
  setDownloadClicked: () => void;
  onClose: () => void;
}

const getColumnFacts = (fieldType: string, dataFact: any) => {
  if (dataFact) {
    const textString = `First In List is ${dataFact?.min} and Last In List is ${dataFact?.max}`;
    const dateString = `Is between ${dataFact?.min} and  ${dataFact?.max}`;
    const defaultString = `Range is from ${dataFact?.min} to ${dataFact?.max}`;

    switch (fieldType) {
      case ColumnTypes.TEXT:
        return textString;
      case ColumnTypes.DATE:
      case ColumnTypes.DATETIME:
        return dateString;
      case ColumnTypes.EMPTY:
        return '';
      default:
        return defaultString;
    }
  } else {
    return '';
  }
};

const CleanActionsModal: FC<CleanActionsModalProps> = ({
  open,
  setDownloadClicked,
  onClose
}) => {
  const params = useParams();

  const isDownloadCleanedActionsEnabledByPlan = useSelector(
    (state: RootState) =>
      state?.AppData?.currentPlanFeatures?.downloadCleanedFile
  );
  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const projectId = useSelector(
    (state: RootState) => state?.projectBoard.currentProjectData?.id
  );

  const [getDataCleaningStatistics, { isLoading }] =
    useLazyGetDataCleaningStatisticsQuery();
  const [getFeatureAccess] = useLazyGetFeatureAccessQuery();

  const [dataCleaningStatistics, setDataCleaningStatistics] =
    useState<CleanResponse | null>(null);
  const [showDuplicateRows, setShowDuplicateRows] = useState(false);

  useEffect(() => {
    const fetchDataCleaningStatistics = async () => {
      const response = await getDataCleaningStatistics(
        params?.projectId || projectId
      );

      if (response.data) setDataCleaningStatistics(response.data);
    };

    if (params?.projectId || projectId) fetchDataCleaningStatistics();
  }, [params?.projectId, projectId]);

  const tableColumns = useMemo(
    () => [
      {
        Header: () => <span>#</span>,
        accessor: 'index',
        width: '5%',
        Cell: ({ cell }: any) => (
          <>{convertToExcelColumnNumber(Number(cell.row.id) + 1)}</>
        )
      },
      {
        Header: () => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              columnGap: 2,
              width: 'min-content'
            }}
          >
            <span style={{ width: 'max-content' }}>Column Name</span>
          </div>
        ),
        accessor: 'fieldname',
        width: '20%',
        Cell: ({ row }: any) => {
          const typographyRef = useRef<HTMLDivElement>();

          const showTooltip =
            typographyRef?.current &&
            typographyRef?.current?.getBoundingClientRect().height > 19
              ? true
              : false;

          return (
            <>
              {row.original.contains_sensitive === true ? (
                <Grid container spacing={1} display='flex' alignItems='center'>
                  <Grid item>
                    <IconButton sx={{ mb: '5px' }}>
                      <FaIcon
                        icon='user-lock'
                        typographyProps={{ variant: 'sm', color: 'primary' }}
                      />
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <MDBox
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      <Tooltip
                        title={showTooltip ? row.original.fieldname : ''}
                        placement='top'
                      >
                        <MDTypography
                          ref={typographyRef}
                          color='info'
                          variant='sm'
                          sx={{
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-all'
                          }}
                        >
                          {row.original.fieldname}
                        </MDTypography>
                      </Tooltip>
                    </MDBox>
                  </Grid>
                </Grid>
              ) : (
                <MDBox
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  <Tooltip
                    title={showTooltip ? row.original.fieldname : ''}
                    placement='top'
                  >
                    <MDTypography
                      ref={typographyRef}
                      color='info'
                      variant='sm'
                      sx={{ textOverflow: 'ellipsis', wordBreak: 'break-all' }}
                    >
                      {row.original.fieldname}
                    </MDTypography>
                  </Tooltip>
                </MDBox>
              )}
            </>
          );
        }
      },
      {
        Header: 'Cleaned Types',
        accessor: 'cleaned',
        width: '20%',
        Cell: ({ row }: any) => {
          const cleanedTypes = Object.keys(row.original.cleaned_count);
          const cleanedCount: number[] = Object.values(
            row.original.cleaned_count
          );

          if (cleanedTypes.length)
            return (
              <MDBox
                display='flex'
                direction='row'
                flexWrap='wrap'
                maxWidth='400px'
              >
                {cleanedTypes.map((type: string, index: number) => {
                  if (cleanedCount[index] > 0)
                    return (
                      <MDBox
                        key={type}
                        sx={{
                          border: `1px solid ${getFontBorderColor(type)}`,
                          width: 'fit-content',
                          paddingY: '2.5px',
                          paddingX: '5px',
                          borderRadius: '5px',
                          backgroundColor: `${getLabelColor(type)}`,
                          margin: 0.4
                        }}
                      >
                        <MDTypography
                          fontWeight='bold'
                          fontSize='0.7rem'
                          sx={{
                            color: getFontBorderColor(type),
                            textAlign: 'center'
                          }}
                        >
                          {`${getCleanLabel(type)}  (${cleanedCount[index]})`}
                        </MDTypography>
                      </MDBox>
                    );
                  else return <></>;
                })}
              </MDBox>
            );
          else return <>-</>;
        }
      },
      {
        Header: 'Data Type',
        accessor: 'treat_as_type'
      },
      {
        Header: 'Missing Values',
        accessor: 'empty_rows',
        width: '5%'
      },
      {
        Header: 'Unique Values',
        accessor: 'unique_values',
        width: '5%'
      },
      {
        Header: 'Facts',
        accessor: 'facts',
        Cell: ({ row }: any) => {
          const fact = getColumnFacts(
            row.original.treat_as_type,
            parseJSON(row.original.data_facts)
          );

          return (
            <Tooltip
              title={
                fact.length > 500
                  ? getColumnFacts(
                      row.original.treat_as_type,
                      parseJSON(row.original.data_facts)
                    )
                  : ''
              }
              placement='top'
            >
              <MDTypography
                color='info'
                variant='sm'
                sx={{ textOverflow: 'wrap' }}
              >
                {fact.slice(0, 500)}
              </MDTypography>
            </Tooltip>
          );
        },
        width: '40%'
      }
    ],
    []
  );

  const duplicateRowsTableHeaders = useMemo(() => {
    if (
      dataCleaningStatistics &&
      dataCleaningStatistics?.cleaned_rows
        ?.map((obj) => obj.dirt_type)
        .includes('FULL_DUP_ROWS')
    ) {
      const tableData: any = parseJSON(
        (dataCleaningStatistics?.cleaned_rows as any)?.find(
          (obj: CleanedRow) => obj.dirt_type === 'FULL_DUP_ROWS'
        ).dirt_examples
      );

      const columnHeaders = tableData?.columns;
      const formattedColumnHeaders = columnHeaders?.map((obj: string) => ({
        Header: obj,
        accessor: (row: any) => row[obj]
      }));

      return formattedColumnHeaders;
    }
  }, [dataCleaningStatistics]);

  const duplicateRowsTableData = useMemo(() => {
    if (
      dataCleaningStatistics &&
      dataCleaningStatistics?.cleaned_rows
        ?.map((obj) => obj.dirt_type)
        .includes('FULL_DUP_ROWS')
    ) {
      const tableData: any = parseJSON(
        (dataCleaningStatistics?.cleaned_rows as any)?.find(
          (obj: CleanedRow) => obj?.dirt_type === 'FULL_DUP_ROWS'
        ).dirt_examples
      );

      const columnHeaders = tableData?.columns;
      const rowData = tableData?.data;
      const formattedRowData = [];

      for (let i = 0; i < rowData?.length; i++) {
        const item = rowData[i];
        const obj: Record<string, any> = {};

        for (let j = 0; j < columnHeaders?.length; j++)
          obj[columnHeaders[j]] = item[j];

        formattedRowData?.push(obj);
      }

      return formattedRowData as any;
    }
  }, [dataCleaningStatistics]);

  const handleDownloadClicked = async () => {
    if (subscriptionFlag) {
      // If feature not accessible by plan then return
      const response = await getFeatureAccess(
        FeatureTypes.DOWNLOAD_CLEANED_FILE
      );

      if (response?.isSuccess) {
        sendAmplitudeData(CLICKED_DOWNLOAD_CLEANED_FILE);
        if (open) onClose();

        setTimeout(() => {
          setDownloadClicked();
        }, 500);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      sx={{
        zIndex: 100,
        '& .MuiPaper-root': {
          width: '90%',
          maxWidth: '100% !important',
          position: 'relative',
          paddingX: '10px',
          overflow: 'visible'
        }
      }}
      id='clean-actions-modal'
    >
      <CloseButton onClose={() => onClose()} />
      {isLoading || !dataCleaningStatistics ? (
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            py: '68px',
            my: '21%'
          }}
        >
          <CircularProgress color='secondary' />
        </DialogContent>
      ) : (
        <>
          <DialogContent>
            <MDBox display='flex' justifyContent='flex-end'>
              <MDTypography variant='h5'>{`Imported ${
                dataCleaningStatistics?.file?.imported_rows ||
                dataCleaningStatistics?.file?.file_rows
              }  Rows / ${
                dataCleaningStatistics?.file?.imported_columns ||
                dataCleaningStatistics?.file?.file_columns
              } Columns`}</MDTypography>
            </MDBox>
            {dataCleaningStatistics?.cleaned_rows.length ? (
              <MDBox display='flex' flexDirection='column' mb={3}>
                <MDTypography variant='h3' textGradient color='info' mb={2}>
                  Cleaned Rows
                </MDTypography>
                <MDBox
                  display='flex'
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  mb={2}
                >
                  <MDBox display='flex' direction='row'>
                    {dataCleaningStatistics?.cleaned_rows.map(
                      (obj: CleanedRow) => (
                        <MDBox
                          key={obj.dirt_type}
                          sx={{
                            border: `1px solid ${getFontBorderColor(
                              obj.dirt_type
                            )}`,
                            width: 'fit-content',
                            paddingY: '2.5px',
                            paddingX: '5px',
                            borderRadius: '5px',
                            backgroundColor: `${getLabelColor(obj.dirt_type)}`,
                            marginRight: 2,
                            cursor:
                              obj.dirt_type === 'EMPTY_ROWS'
                                ? 'default'
                                : 'pointer'
                          }}
                          onClick={() => {
                            if (obj.dirt_type !== 'EMPTY_ROWS')
                              setShowDuplicateRows(!showDuplicateRows);
                          }}
                        >
                          <MDTypography
                            fontWeight='bold'
                            fontSize='0.7rem'
                            sx={{ color: getFontBorderColor(obj.dirt_type) }}
                          >
                            {`${getCleanLabel(obj.dirt_type)} (${
                              obj.dirt_quantity
                            })`}
                          </MDTypography>
                        </MDBox>
                      )
                    )}
                  </MDBox>
                  {dataCleaningStatistics?.cleaned_rows
                    ?.map((obj) => obj.dirt_type)
                    .includes('FULL_DUP_ROWS') && (
                    <IconButton
                      onClick={() => setShowDuplicateRows(!showDuplicateRows)}
                    >
                      <FaIcon
                        icon={showDuplicateRows ? 'angle-up' : 'angle-down'}
                      />
                    </IconButton>
                  )}
                </MDBox>
                <Collapse in={showDuplicateRows} unmountOnExit>
                  <Grid container>
                    <DataTable
                      table={{
                        columns: duplicateRowsTableHeaders,
                        rows: duplicateRowsTableData
                      }}
                      showTotalEntries={false}
                      entriesPerPage={{
                        defaultValue: duplicateRowsTableData?.length || 0,
                        entries: []
                      }}
                      maxHeight='50vh'
                      width='max-content'
                    />
                  </Grid>
                </Collapse>
              </MDBox>
            ) : (
              <></>
            )}
            <MDBox display='flex' alignItems='center' columnGap={0.5}>
              <MDTypography variant='h3' textGradient color='info'>
                Cleaned Columns
              </MDTypography>
            </MDBox>
            <MDBox>
              <DataTable
                table={{
                  columns: tableColumns,
                  rows: dataCleaningStatistics?.cleaned_columns || []
                }}
                showTotalEntries={false}
                entriesPerPage={{
                  defaultValue:
                    dataCleaningStatistics?.cleaned_columns.length || 10,
                  entries: []
                }}
                cleanedActionTable
                customStyle={{ overflowY: 'hidden' }}
              />
            </MDBox>
          </DialogContent>
          <DialogActions
            sx={{
              position: 'sticky',
              right: 0,
              bottom: 20,
              borderRadius: '10px',
              justifyContent: 'right',
              mb: 1,
              mx: 1.5,
              py: 1.5
            }}
          >
            <MDBox
              display='flex'
              borderRadius='10px'
              gap={2}
              sx={{ background: 'white' }}
            >
              <MDButton
                color='secondary'
                variant='outlined'
                onClick={onClose}
                circular={false}
              >
                Close
              </MDButton>
              <MDButton
                color='primary'
                variant='gradient'
                circular={false}
                onClick={handleDownloadClicked}
              >
                <div style={{ display: 'flex' }}>
                  {subscriptionFlag &&
                    (isDownloadCleanedActionsEnabledByPlan ? (
                      <FaIcon
                        icon='lock-open'
                        style={{ marginRight: '5px', marginBottom: '2px' }}
                        typographyProps={{ variant: 'lg', color: 'white' }}
                      />
                    ) : (
                      <FaIcon
                        icon='lock'
                        style={{ marginRight: '5px', marginBottom: '2px' }}
                        typographyProps={{ variant: 'lg', color: 'white' }}
                      />
                    ))}
                  <div style={{ display: 'flex', marginTop: '2px' }}>
                    Download Cleaned File
                  </div>
                </div>
              </MDButton>
            </MDBox>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CleanActionsModal;
